<script lang="ts">
  import CommonRulerContext from '#lib/components/contexts/CommonRulerContext.svelte';
  import { getTraceColor, traceBlue, traceGreen, traceOrange } from '#lib/overview/colorPalette.js';
  import { parseUnit } from '#lib/overview/utils/units.js';
  import { faDownload, faUpload } from '@fortawesome/free-solid-svg-icons';
  import { TraceList, type TraceStyleSheet, type DisplayUnitPreference } from 'libchartium';
  import Fa from 'svelte-fa';
  import type { PageData } from './$types';
  import { Dataset } from '#lib/overview/datasetNames.js';
  import { first, flatMap } from '#lib/utils/generators.js';
  import YAxisAlignmentContext from '#lib/components/contexts/YAxisAlignmentContext.svelte';
  import CommonXRangeContext from '#lib/components/contexts/CommonXRangeContext.svelte';
  import ChartiumChart from '#lib/components/ChartiumChart.svelte';
  import ImportSeriesModal from './ImportSeriesModal.svelte';
  import { getChartiumTraces } from '#lib/charts/dataConversion.js';

  let { data }: { data: PageData } = $props();

  const sumStyle: TraceStyleSheet[string] = {
    color: getTraceColor('sum'),
    fill: 'to-zero-y',
    'fill-opacity': 1,
  };

  let plots: {
    title: string;
    traces: TraceList;
    displayUnit?: DisplayUnitPreference;
  }[] = $state([]);

  export function combineTraceData<D extends keyof PageData & Dataset>(
    traces: Iterable<[string, D, string?]>,
  ): Map<string, { id?: string; x: Date[]; y: number[] }> {
    return new Map(
      flatMap(traces, ([label, dataset, id]) =>
        label === '...'
          ? (data[dataset]?.entries() ?? [])
          : [
              [
                label,
                {
                  ...(first(data[dataset]?.values() ?? []) ?? {
                    x: [],
                    y: [],
                  }),
                  id,
                },
              ],
            ],
      ),
    );
  }

  $effect(() => {
    (async () => {
      if (data.mode === 'hp') {
        plots = [
          {
            title: 'Transfer (MB / s)',
            traces: await getChartiumTraces({
              data: combineTraceData([
                ['Sum', Dataset.HP_TRANSFER_SUM, 'hp-transfer-sum'],
                ['...', Dataset.HP_TRANSFER_PERSYSTEM],
              ]),
              unit: parseUnit('MB/s'),
              style: { 'hp-transfer-sum': sumStyle },
            }),
          },
          {
            title: 'Workload (IO / s)',
            traces: await getChartiumTraces({
              data: combineTraceData([
                ['Sum', Dataset.HP_WORKLOAD_SUM, 'hp-workload-sum'],
                ['...', Dataset.HP_WORKLOAD_PERSYSTEM],
              ]),
              unit: parseUnit('IO/s'),
              style: { 'hp-workload-sum': sumStyle },
            }),
          },
          {
            title: 'Total R+W Blocksize History',
            traces: await getChartiumTraces({
              data: combineTraceData([
                ['Read Blocksize', Dataset.HP_BLOCKSIZE_READ_AVG],
                ['Write Blocksize', Dataset.HP_BLOCKSIZE_WRITE_AVG],
              ]),
              unit: parseUnit('kB'),
            }),
          },
          {
            title: 'Total Capacity History',
            traces: await getChartiumTraces({
              data: combineTraceData([
                ['Logical Capacity', Dataset.HP_CAPACITY_LOGICAL_SUM],
                ['Physical Capacity', Dataset.HP_CAPACITY_PHYSICAL_SUM],
                ['Subscribed Capacity', Dataset.HP_CAPACITY_SUBSCRIBED_SUM],
              ]),
              unit: parseUnit('TB'),
            }),
          },
          {
            title: 'Physical Capacity per System',
            traces: await getChartiumTraces({
              data: data[Dataset.HP_CAPACITY_PHYSICAL_PERSYSTEM],
              unit: parseUnit('TB'),
            }),
          },
        ];
      } else if (data.mode === 'emc') {
        plots = [
          {
            title: 'Transfer (MB / s)',
            traces: await getChartiumTraces({
              data: combineTraceData([
                ['Sum', Dataset.EMC_TRANSFER_SUM, 'emc-transfer-sum'],
                ['...', Dataset.EMC_TRANSFER_PERSYSTEM],
              ]),
              unit: parseUnit('MB/s'),
              style: { 'emc-transfer-sum': sumStyle },
            }),
          },
          {
            title: 'Workload (IO / s)',
            traces: await getChartiumTraces({
              data: combineTraceData([
                ['Sum', Dataset.EMC_WORKLOAD_SUM, 'emc-workload-sum'],
                ['...', Dataset.EMC_WORKLOAD_PERSYSTEM],
              ]),
              unit: parseUnit('IO/s'),
              style: { 'emc-workload-sum': sumStyle },
            }),
          },
          {
            title: 'Total Capacity',
            traces: await getChartiumTraces({
              data: combineTraceData([
                ['Physical Capacity', Dataset.EMC_CAPACITY_PHYSICAL_SUM, 'dash-emc-phys'],
                ['Network Capacity', Dataset.EMC_CAPACITY_NET_SUM, 'dash-emc-net'],
                ['Subscribed Capacity', Dataset.EMC_CAPACITY_SUBSCRIBED_SUM, 'dash-emc-sub'],
              ]),
              unit: parseUnit('TB'),
              style: {
                'dash-emc-phys': { color: traceBlue },
                'dash-emc-net': { color: traceOrange },
                'dash-emc-sub': { color: traceGreen },
              },
            }),
          },
          {
            title: 'Physical Capacity per System',
            traces: await getChartiumTraces({
              data: data[Dataset.EMC_CAPACITY_PHYSICAL_PERSYSTEM],
              unit: parseUnit('TB'),
            }),
          },
        ];
      }
    })();
  });

  function downloadData() {
    const a = document.createElement('a');
    a.href = '/timeSeries';
    a.download = 'time_series.json';
    a.click();
  }

  let importSeriesModalOpen = $state(false);
</script>

<section class="flex items-center justify-between">
  <h1>Dashboard</h1>
  <menu class="space-x-2">
    <button
      class="btn btn-primary btn-sm btn-circle btn-outline"
      title="Export time data"
      onclick={downloadData}
    >
      <Fa icon={faDownload} />
    </button>
    <button
      class="btn btn-primary btn-sm btn-circle btn-outline"
      title="Import time data"
      onclick={() => (importSeriesModalOpen = true)}
    >
      <Fa icon={faUpload} />
    </button>
  </menu>
</section>
<section class="grid auto-rows-100 gap-4 pb-4">
  <CommonXRangeContext>
    <YAxisAlignmentContext>
      <CommonRulerContext>
        {#each plots as plot}
          <ChartiumChart {...plot} class="bg-base-100" />
        {/each}
        {#if plots.length === 0}
          <p>Unknown mode</p>
        {/if}
      </CommonRulerContext>
    </YAxisAlignmentContext>
  </CommonXRangeContext>
</section>

<ImportSeriesModal bind:open={importSeriesModalOpen} />

<style>
  section :global(.plot .bottom-legend) {
    max-height: 185px;
    overflow-y: auto;
  }
</style>
